import { useEffect, useState } from 'react';
import { axiosInstance } from '../../App';
import './PatientClinicalPanel.scss';

interface PatientClinicalInfo {
  id: number;
  name: string;
  age: number | null;
  birthDate: string | null;
  gender: string | null;
  documentType: string | null;
  document: string | null;
  clinicHistoryId: string | null;
  coverageName: string | null;
  planName: string | null;
  benefitNumber: string | null;
}

interface PatientAntecedentItem {
  id: number;
  text: string;
  date: string | null;
}

interface PatientAntecedents {
  personal: PatientAntecedentItem[];
  family: PatientAntecedentItem[];
  allergies: PatientAntecedentItem[];
  medication: PatientAntecedentItem[];
}

interface PatientClinicalSummaryResponse {
  patient: PatientClinicalInfo;
  antecedents: PatientAntecedents;
}

interface PatientClinicalPanelViewProps {
  patient: PatientClinicalInfo;
  antecedents: PatientAntecedents;
  defaultAntecedentsExpanded?: boolean;
  className?: string;
}

export interface PatientClinicalPanelByIdProps {
  patientId: number;
  defaultAntecedentsExpanded?: boolean;
  className?: string;
}

const loadPatientClinicalSummary = (patientId: number): Promise<PatientClinicalSummaryResponse> =>
  axiosInstance
    .get<PatientClinicalSummaryResponse>(`/api/secured/patients/${patientId}/clinical-summary`)
    .then((response) => response.data);

const genderLabel = (gender: string | null): string | null => {
  if (gender === 'F') return 'Femenino';
  if (gender === 'M') return 'Masculino';
  return gender;
};

const patientInitials = (name: string): string => name
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map((part) => part.charAt(0).toUpperCase())
  .join('') || 'P';

function PatientClinicalPanelView({
  patient,
  antecedents,
  defaultAntecedentsExpanded = false,
  className = '',
}: PatientClinicalPanelViewProps) {
  const [antecedentsExpanded, setAntecedentsExpanded] = useState(defaultAntecedentsExpanded);
  const categories = [
    ['Personal', antecedents?.personal ?? []],
    ['Familiar', antecedents?.family ?? []],
    ['Alergias', antecedents?.allergies ?? []],
    ['Medicación', antecedents?.medication ?? []],
  ] as const;
  const totalAntecedents = categories.reduce((total, [, items]) => total + items.length, 0);
  const hasAntecedents = totalAntecedents > 0;
  const formattedGender = genderLabel(patient.gender);

  const toggleAntecedents = () => {
    setAntecedentsExpanded((current) => !current);
  };

  return (
    <section className={`patient-clinical-panel ${className}`.trim()} aria-label="Información clínica del paciente">
      <div className="patient-clinical-panel__patient">
        <div className="patient-clinical-panel__avatar" aria-hidden="true">{patientInitials(patient.name)}</div>
        <div className="patient-clinical-panel__patient-main">
          <span className="patient-clinical-panel__name">{patient.name}</span>
          <div className="patient-clinical-panel__details patient-clinical-panel__details--primary">
            {patient.age !== null ? (
              <span><i className="pi pi-calendar" />{patient.age} años{patient.birthDate ? ` · ${patient.birthDate}` : ''}</span>
            ) : null}
            {formattedGender ? <span><i className="pi pi-user" />{formattedGender}</span> : null}
            {patient.documentType && patient.document ? (
              <span><i className="pi pi-id-card" />{patient.documentType}: {patient.document}</span>
            ) : null}
            {patient.clinicHistoryId ? <span><i className="pi pi-folder" />HC: {patient.clinicHistoryId}</span> : null}
          </div>
          <div className="patient-clinical-panel__details">
            {patient.coverageName ? (
              <span><i className="pi pi-shield" />{patient.coverageName}{patient.planName ? ` · ${patient.planName}` : ''}</span>
            ) : null}
            {patient.benefitNumber ? <span>N.º afiliado: {patient.benefitNumber}</span> : null}
          </div>
        </div>
      </div>

      <div className="patient-clinical-panel__antecedents">
        <button
          type="button"
          className="patient-clinical-panel__antecedents-header"
          onClick={toggleAntecedents}
          aria-expanded={antecedentsExpanded}
        >
          <span className="patient-clinical-panel__antecedents-heading">
            <span className="patient-clinical-panel__antecedents-icon" aria-hidden="true">
              <i className="pi pi-history" />
            </span>
            <span className="patient-clinical-panel__antecedents-title">Antecedentes</span>
            {totalAntecedents > 0 ? <span className="patient-clinical-panel__count">{totalAntecedents}</span> : null}
          </span>
          <i className={`pi ${antecedentsExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`} aria-hidden="true" />
        </button>

        {antecedentsExpanded ? (
          <div className="patient-clinical-panel__antecedents-body">
            {!hasAntecedents ? (
              <p className="patient-clinical-panel__empty">No hay antecedentes registrados.</p>
            ) : (
              <div className="patient-clinical-panel__categories">
                {categories.map(([title, items]) => items.length > 0 ? (
                  <div className="patient-clinical-panel__category" key={title}>
                    <div className="patient-clinical-panel__category-title">{title}</div>
                    <ul className="patient-clinical-panel__items">
                      {items.map((item) => (
                        <li key={item.id} className="patient-clinical-panel__item">
                          {item.text}{item.date ? <span className="patient-clinical-panel__date"> ({item.date})</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null)}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default function PatientClinicalPanelById({
  patientId,
  defaultAntecedentsExpanded = false,
  className = '',
}: PatientClinicalPanelByIdProps) {
  const [summary, setSummary] = useState<PatientClinicalSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let active = true;
    setSummary(null);
    setLoading(true);
    setError(null);

    loadPatientClinicalSummary(patientId)
      .then((response) => {
        if (active) setSummary(response);
      })
      .catch((requestError) => {
        if (!active) return;
        const message = requestError?.response?.data?.message
          || requestError?.message
          || 'No se pudo cargar la información clínica del paciente.';
        setError(message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [patientId, reloadToken]);

  if (loading) {
    return (
      <div className={`patient-clinical-panel patient-clinical-panel--loading ${className}`.trim()} role="status">
        <i className="pi pi-spinner pi-spin" aria-hidden="true" />
        <span>Cargando información del paciente...</span>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className={`patient-clinical-panel patient-clinical-panel--error ${className}`.trim()} role="alert">
        <i className="pi pi-exclamation-circle" aria-hidden="true" />
        <span>{error || 'No se encontró la información del paciente.'}</span>
        <button type="button" onClick={() => setReloadToken((current) => current + 1)}>Reintentar</button>
      </div>
    );
  }

  return (
    <PatientClinicalPanelView
      key={summary.patient.id}
      patient={summary.patient}
      antecedents={summary.antecedents}
      defaultAntecedentsExpanded={defaultAntecedentsExpanded}
      className={className}
    />
  );
}
